import React, { useEffect, useRef, useState, useCallback } from "react";
import "../../constants/styles/chatPanel.scss";
import "../../constants/styles/chatBubble.scss";
import "../../constants/styles/loading.scss";
import ChatBubble from "./chatComponents/ChatBubble";
import { chatPanelTexts } from "../../constants/textContent";

const ChatPanel = ({ userId, conversationId, initialBotMessage, endChat, showEndChatButton, onEndChatTriggered }) => {
    const messagesEndRef = useRef(null);
    const initialMessageAddedRef = useRef(false);
    const [history, setHistory] = useState([]);
    const [message, setMessage] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isEndChatClicked, setIsEndChatClicked] = useState(false);
    const [messageStartTime, setMessageStartTime] = useState(null);
    const [keystrokes, setKeystrokes] = useState([]);
    const [chatStartTime, setChatStartTime] = useState(null);
    const [chatEndTime, setChatEndTime] = useState(null);
    const inputRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const updateHistory = useCallback((type, content) => {
        setHistory((prevHistory) => [...prevHistory, { type, content }]);
    }, []);

    const handleResponse = useCallback((data) => {
        const responseType = data.result.role === "assistant" ? "bot" : "user";
        updateHistory(responseType, data.result.content);
        if (data.endChat === true) {
            alert(chatPanelTexts.endChatAlert);
            onEndChatTriggered();
        }
    }, [updateHistory, onEndChatTriggered]);

    const sendUserMessage = useCallback(async () => {
        if (!message.trim()) return;
        const content = message.trim();
        const endTime = new Date().toISOString();
        setMessage("");
        updateHistory("user", content);
        scrollToBottom();
        if (messageStartTime) {
            setKeystrokes(ks => [...ks, { content, startTime: messageStartTime, endTime }]);
        }
        setMessageStartTime(null); // Reset start time after sending the message
        setIsTyping(true);

        try {
            const response = await fetch(
                "https://www.cs.toronto.edu/~brli/intakebot/api/chatbot",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ ID: conversationId, content }),
                }
            );

            if (!response.ok) {
                throw new Error(`${chatPanelTexts.errorMessage} ${response.status}`);
            }
            const data = await response.json();
            setIsTyping(false);
            handleResponse(data);
        } catch (error) {
            setIsTyping(false);
            console.error(`${chatPanelTexts.errorMessage} ${error}`);
        } finally {
            scrollToBottom();
        }
    }, [message, conversationId, scrollToBottom, updateHistory, handleResponse, messageStartTime]);

    useEffect(() => {
        if (conversationId && initialBotMessage && !initialMessageAddedRef.current) {
            updateHistory("bot", initialBotMessage);
            initialMessageAddedRef.current = true;
            setChatStartTime(new Date().toISOString());  // Set chat start time on first message
        }
    }, [conversationId, initialBotMessage, updateHistory]);

    useEffect(() => {
        scrollToBottom();
    }, [history, scrollToBottom]);

    const handleMessageChange = useCallback((event) => {
        setMessage(event.target.value);
        if (!messageStartTime) {
            setMessageStartTime(new Date().toISOString()); // Set start time when typing the first character
        }
    }, [messageStartTime]);

    const handleKeyPress = useCallback((event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendUserMessage();
        }
    }, [sendUserMessage]);

    const handleEndChatClick = () => {
        setIsEndChatClicked(true);
        setChatEndTime(new Date().toISOString());  // Set chat end time
        endChat();
    };

    const formatDuration = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes} min ${remainingSeconds} sec`;
    };


    useEffect(() => {
        const download = (keystrokes, fileName, contentType) => {
            // Calculate average time per message
            const messageTimes = keystrokes.map(ks => (new Date(ks.endTime) - new Date(ks.startTime)) / 1000);
            const averageTimePerMessage = messageTimes.reduce((a, b) => a + b, 0) / keystrokes.length;

            // Calculate total chat duration
            const totalChatDuration = (new Date(chatEndTime) - new Date(chatStartTime)) / 1000;

            // Format durations
            const formattedAverageTime = formatDuration(averageTimePerMessage);
            const formattedTotalDuration = formatDuration(totalChatDuration);

            // Add calculations to the content to be downloaded
            const contentToDownload = {

                averageTimePerMessage: formattedAverageTime,
                totalChatDuration: formattedTotalDuration,
                keystrokes: keystrokes
            };

            const a = document.createElement("a");
            const file = new Blob([JSON.stringify(contentToDownload, null, 2)], { type: contentType });
            a.href = URL.createObjectURL(file);
            a.download = fileName;
            a.click();
        };

        if (isEndChatClicked) {
            download(keystrokes, `id${userId}.txt`, 'text/plain');
        }
    }, [isEndChatClicked, keystrokes, userId, chatEndTime, chatStartTime]);

    return (
        <div className="ChatPanel">
            {isEndChatClicked ? (
                <div className="loadingScreen">
                    <div className="loading-spinner"></div>
                    <div>Generating summary...</div>
                </div>
            ) : (
                <>
                    <div className="MessagesContainer custom-scrollbars__content">
                        {history.map((message, index) => (
                            <ChatBubble key={index} type={message.type}>
                                {message.content}
                            </ChatBubble>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                    <div className="TypingIndicator">
                        {isTyping && (
                            <>
                                <span>Intake Bot is typing</span>
                                <span className="dots">
                                    <span>.</span><span>.</span><span>.</span>
                                </span>
                            </>
                        )}
                    </div>
                    <div className="SendMessage">
                        <textarea
                            ref={inputRef}
                            placeholder={chatPanelTexts.placeholder}
                            value={message}
                            onChange={handleMessageChange}
                            onKeyPress={handleKeyPress}
                            aria-label={chatPanelTexts.placeholder}
                            className="InputField"
                        />
                        {showEndChatButton && (
                            <button
                                className={`EndChatButton ${isEndChatClicked ? 'disabled' : ''}`}
                                onClick={handleEndChatClick}
                                disabled={isEndChatClicked}
                            >
                                End Chat
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ChatPanel;
